import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ICard, IColumn, IError } from '../../../../../interfaces';
import { Subject, takeUntil } from 'rxjs';
import { CrmService } from '../../../../../services/crm.service';
import { CardForm } from './card.interface';
import { NotificationService } from '../../../../../services/notifications.service';
import { CURRENCY_CODE, DEFAULT_ERROR, ERROR_MESSAGES } from '../../../../../enum/constants';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseService } from '../../../../../services/base.service';
import { DialogService } from '../../../../../services/dialog.service';
import { CustomAttributesService } from '../../../../../services/custom-attributes.service';
import { ICA } from '../../../../../tabs/tab-deals-settings-fields/tab-deals-settings-fields.interface';
import { Router } from '@angular/router';
import { Tab } from '../../../../tabs/interfaces/tab.interface';
import { CardTab } from '../../../../tabs/enums/tabs.enum';

@Component({
  selector: 'app-card-details',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CardComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();
  private cardId!: string;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  public card!: ICard;
  public cardForm!: FormGroup<CardForm>;
  public loaders = {
    card: false,
    columns: false,
    customAttributes: false,
  };
  public columns: IColumn[] = [];

  public isEditTitle: boolean = false;
  public hoverColumn = {
    index: 0,
    bgColor: '',
    textColor: '',
  };

  public tabs: Tab<CardTab>[] = [{ name: 'general', default: true, value: CardTab.GENERAL }];

  public selectedTab!: Tab<CardTab>;
  public customAttributes: ICA[] = [];
  public isEditCard: boolean = false;

  public colors: string[] = [
    '#fbc6bd',
    '#bde2ff',
    '#ffb4ce',
    '#d1ffc3',
    '#fbe9b8',
    '#f8edae',
    '#a3e1ce',
    '#b7eded',
    '#b4b9f3',
    '#ceb6ef',
    '#dfe6e9',
    '#ffffff',
  ];

  constructor(
    private readonly router: Router,
    private readonly crmService: CrmService,
    public readonly baseService: BaseService,
    private readonly customAttributesService: CustomAttributesService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    if (this.payload?.card_id) {
      this.cardId = this.payload.card_id;
    }
    this.setParams();
    this.initCard();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initForm() {
    const customAttributesControls: Record<string, any> = {};
    this.customAttributes.forEach((ca: ICA) => {
      customAttributesControls[ca.key] = new FormControl(this.card.custom_attributes[ca.key]);
    });
    this.cardForm = new FormGroup<CardForm>({
      name: new FormControl(this.card.name),
      price: new FormControl(this.card.price),
      currency: new FormControl(this.card.currency),
      column_id: new FormControl(this.card.column_id),
      funnel_id: new FormControl(this.card.column_id),
      bg_color: new FormControl(this.card.bg_color),
      custom_attributes: new FormGroup<Record<string, any>>({
        ...customAttributesControls,
      }),
    });
  }

  private setParams() {
    this.router.navigate([], {
      queryParams: {
        card: this.cardId,
      },
    });
  }

  private initCard() {
    this.loaders.card = true;
    this.loaders.columns = true;
    this.loaders.customAttributes = true;
    this.crmService.getCard(this.cardId).subscribe({
      next: (res) => {
        this.card = res;
        this.loaders.card = false;

        this.initColumns();
        this.initCustomAttributes();
      },
      error: () => {
        this.loaders.card = false;
        this.loaders.columns = false;
      },
    });
  }

  private initColumns() {
    this.loaders.columns = true;
    const params = {
      funnel_id: this.card.funnel_id,
      page: 1,
      take: 50,
    };
    this.crmService
      .getAllColumns(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.columns = [...res.items];
          this.loaders.columns = false;
        },
        error: () => {
          this.loaders.columns = false;
        },
      });
  }

  public isHoverArrow(hover: boolean, columnBgColor: string, columnTextColor: string, index: number): void {
    this.hoverColumn = {
      index: hover ? index : 0,
      bgColor: hover ? columnBgColor : '',
      textColor: hover ? columnTextColor : '',
    };
  }

  public onGetColumnColor(column: any, i: number, colorType: string) {
    const targetColumn: any = this.columns.find((v) => v.id === this.card.column_id);
    if (!targetColumn) return { color: '', is_fill: false };

    const targetColor = colorType === 'bg' ? targetColumn.bg_color : targetColumn.text_color;
    const hoveredColor = colorType === 'bg' ? this.hoverColumn.bgColor : this.hoverColumn.textColor;
    const currentColor = colorType === 'bg' ? column.bg_color : column.text_color;

    if (!hoveredColor && i <= this.columns.indexOf(targetColumn)) return { color: targetColor, is_fill: true };
    else if (hoveredColor && this.hoverColumn.index >= 0 && i <= this.hoverColumn.index)
      return { color: hoveredColor, is_fill: true };
    return { color: currentColor, is_fill: false };
  }

  public columnStyle(column: IColumn, i: number): Record<string, string> {
    const bgColor = this.onGetColumnColor(column, i, 'bg');
    const txtColor = this.onGetColumnColor(column, i, 'txt');

    return { '--column-bg-color': bgColor.color, '--column-text-color': txtColor.color };
  }

  public isColumnColor(column: IColumn, i: number): boolean {
    const bgColor = this.onGetColumnColor(column, i, 'bg');
    const txtColor = this.onGetColumnColor(column, i, 'txt');

    return bgColor.is_fill && txtColor.is_fill;
  }

  public onChangeColumn(column_id: string): void {
    if (column_id === this.card.column.id) {
      return;
    }

    // this.current_id = column_id;

    this.crmService
      .updateCard(this.card.id, { column_id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ICard) => {
          this.card = {} as ICard;
          this.card = res;
        },
        error: (e): void => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public onChangeTab(selectedTab: any): void {
    this.selectedTab = selectedTab;
  }

  private initCustomAttributes() {
    this.customAttributesService
      .getAllCA({
        funnel_id: this.card.funnel_id,
        page: 1,
        take: 500,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resCA) => {
          this.customAttributes = resCA?.items || [];
          this.loaders.customAttributes = false;
          this.initForm();
        },
        error: () => {
          this.loaders.customAttributes = false;
        },
      });
  }

  protected readonly CardTab = CardTab;

  editField(ca: any) {
    ca.editable = true;
    this.isEditCard = true;
  }

  onSave() {
    const updateCard: Record<string, any> = {};
    for (const key of Object.keys(this.cardForm.controls)) {
      const control = this.cardForm.get(key);
      if (control instanceof FormControl && control.dirty) {
        updateCard[key] = control.value;
      } else if (control instanceof FormGroup && key === 'custom_attributes') {
        const customAttrs: Record<string, any> = {};
        Object.keys(control.controls).forEach((caKey) => {
          const caControl = control.get(caKey);
          if (caControl?.dirty) {
            customAttrs[caKey] = caControl.value;
          }
        });
        if (Object.keys(customAttrs).length) {
          updateCard[key] = customAttrs;
        }
      }
    }

    this.crmService.updateCard(this.card.id, updateCard).subscribe({
      next: (res: ICard) => {
        this.card = res;
        this.onReset();
      },
    });
  }

  onCancel() {
    if (!this.cardForm.dirty) {
      this.onReset();
      return;
    }

    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.reset_changes',
    });

    dialogRef.subscribe({
      next: (res) => {
        if (res) {
          this.onReset();
        }
      },
    });
  }

  onReset() {
    this.isEditCard = false;
    this.isEditTitle = false;
    this.card.editable = false;
    this.card.column.editable = false;
    this.customAttributes.forEach((ca: ICA) => {
      ca.editable = false;
    });
    this.cardForm.reset(this.card);
  }

  protected readonly CURRENCY_CODE = CURRENCY_CODE;

  onFocusColumn(id: string) {
    console.log('focus');
  }
}
